import createClient from 'openapi-fetch';

import i18n from '../i18n';

import type { components, paths } from './generated/schema';
import type { Middleware } from 'openapi-fetch';

export type Problem = components['schemas']['Problem'];
export type Me = components['schemas']['Me'];

/** Error de la API con su Problem+JSON (docs/05 §5). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem | null,
  ) {
    super(problem?.code ?? `HTTP ${String(status)}`);
    this.name = 'ApiError';
  }
  get code(): string {
    return this.problem?.code ?? 'INTERNAL';
  }
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

/** Última respuesta correcta de la API: base del aviso de sesión por vencer (RF-AUTH-07). */
export const activity = { last: Date.now() };

const middleware: Middleware = {
  onRequest({ request }) {
    request.headers.set('Accept-Language', i18n.language);
    // CSRF de doble envío (docs/06 §4): la cookie __Host-csrf vuelve en el header.
    const csrf = readCookie('__Host-csrf');
    if (UNSAFE.has(request.method) && csrf !== undefined) request.headers.set('X-CSRF-Token', csrf);
    return request;
  },
  onResponse({ response }) {
    if (response.ok) activity.last = Date.now();
    return response;
  },
};

export const api = createClient<paths>({
  // Mismo origen que la web (Caddy o el proxy de Vite). Absoluta: Request no acepta rutas relativas fuera del navegador.
  baseUrl: `${window.location.origin}/api/v1`,
  credentials: 'same-origin',
  // fetch se resuelve en cada pedido (los tests lo reemplazan después de importar el cliente).
  fetch: (request) => globalThis.fetch(request),
});
api.use(middleware);

/** Datos o ApiError: así los errores llegan a TanStack Query y a los formularios. */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response.ok) throw new ApiError(response.status, (error ?? null) as Problem | null);
  return data as T;
}
